import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Brain, Loader2, Sparkles, Target, Dumbbell, Heart, Shield, ChartBar, RefreshCw, CheckCircle, AlertCircle, Send, MessageCircle, Download, Mail, User } from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const FOCUS_OPTIONS = [
  { id: 'speed', label: 'Speed & Acceleration', icon: '⚡' },
  { id: 'strength', label: 'Strength & Power', icon: '💪' },
  { id: 'agility', label: 'Agility & Coordination', icon: '🔄' },
  { id: 'endurance', label: 'Endurance', icon: '🏃' },
  { id: 'mental', label: 'Mental Performance', icon: '🧠' },
  { id: 'recovery', label: 'Recovery & Injury Prevention', icon: '🛡️' }
];

export default function AICoach({ childId, childName }) {
  const [loading, setLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [recommendation, setRecommendation] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedFocus, setSelectedFocus] = useState([]);
  const [specificQuestion, setSpecificQuestion] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  
  // Chat state
  const [showChat, setShowChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const chatEndRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai-coach/recommendations/${childId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setHistory(response.data.recommendations || []);
      
      if (response.data.recommendations?.length > 0) {
        setRecommendation(response.data.recommendations[0]);
      }
    } catch (error) {
      // No previous recommendations - expected for new athletes
    } finally {
      setLoadingHistory(false);
    }
  }, [childId]);

  const loadChatHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/ai-coach/messages/${childId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setChatHistory(response.data.messages?.reverse() || []);
    } catch (error) {
      // No chat history - expected for new athletes
    }
  }, [childId]);

  useEffect(() => {
    loadHistory();
    loadChatHistory();
  }, [loadHistory, loadChatHistory]);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatHistory]);

  const toggleFocus = (focusId) => {
    setSelectedFocus(prev => 
      prev.includes(focusId) 
        ? prev.filter(f => f !== focusId)
        : [...prev, focusId]
    );
  };

  const generateRecommendations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai-coach/recommendations`, {
        child_id: childId,
        focus_areas: selectedFocus.length > 0 ? selectedFocus : null,
        specific_question: specificQuestion || null
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      setRecommendation(response.data);
      setHistory(prev => [response.data, ...prev.slice(0, 9)]);
      
      // Check if workout plan was auto-created
      if (response.data.workout_plan) {
        toast.success('AI recommendations generated with workout plan!');
      } else {
        toast.success('AI recommendations generated!');
      }
      setSpecificQuestion('');
    } catch (error) {
      console.error('AI Coach error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate recommendations');
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async (requestHuman = false) => {
    if (!chatMessage.trim() && !requestHuman) return;
    
    try {
      setSendingMessage(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai-coach/chat`, {
        child_id: childId,
        message: chatMessage || "Please contact me about this athlete.",
        request_human_help: requestHuman
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      // Add to chat history
      const newMessage = {
        id: response.data.message_id,
        message: chatMessage || "Requested human assistance",
        response: response.data.response,
        created_at: new Date().toISOString(),
        forwarded: response.data.forwarded,
        needs_human_followup: response.data.needs_human_followup
      };
      
      setChatHistory(prev => [...prev, newMessage]);
      setChatMessage('');
      
      if (response.data.forwarded) {
        toast.success('Your question has been forwarded to the coaching team!');
      }
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const forwardToHuman = async (messageId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/ai-coach/forward-to-human?message_id=${messageId}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success(response.data.message);
      loadChatHistory();
    } catch (error) {
      toast.error('Failed to forward message');
    }
  };

  const downloadTrainingPlan = async () => {
    try {
      setDownloadingPdf(true);
      const token = localStorage.getItem('token');

      // Use the unified Athlete Dossier endpoint (combines report card + AI plan + 6-week microcycle)
      const response = await axios.post(
        `${API}/dossier/${childId}/generate`,
        { email_athlete: true, use_cached_ai: true },
        {
          responseType: 'blob',
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      const emailStatus = response.headers['x-email-status'];
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Athlete_Dossier_${childName.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      if (emailStatus === 'sent') {
        toast.success('Dossier saved to Media, downloaded, and emailed to the athlete.');
      } else {
        toast.success('Dossier saved to Media and downloaded.');
      }
    } catch (error) {
      console.error('Dossier download error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to generate dossier';
      toast.error(errorMessage);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatRecommendation = (text) => {
    if (!text) return null;
    
    if (typeof text === 'object') {
      return (
        <div className="space-y-6">
          {text.priority_areas?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold uppercase tracking-widest text-[#02b5ff] mb-3">Priority Areas</h4>
              <div className="flex flex-wrap gap-2">
                {text.priority_areas.map((area, idx) => (
                  <span key={idx} className="px-3 py-1 bg-[#02b5ff]/20 text-[#02b5ff] text-sm rounded">
                    {area.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {text.weekly_plan && (
            <div>
              <h4 className="text-sm font-bold uppercase tracking-widest text-[#02b5ff] mb-3">Weekly Plan</h4>
              <div className="bg-zinc-900/50 p-4 rounded space-y-2">
                <p className="text-zinc-300"><span className="text-zinc-500">Sessions/Week:</span> {text.weekly_plan.sessions_per_week}</p>
                <p className="text-zinc-300"><span className="text-zinc-500">Focus:</span> {text.weekly_plan.focus}</p>
                {text.weekly_plan.day_1 && <p className="text-zinc-300"><span className="text-zinc-500">Day 1:</span> {text.weekly_plan.day_1}</p>}
                {text.weekly_plan.day_2 && <p className="text-zinc-300"><span className="text-zinc-500">Day 2:</span> {text.weekly_plan.day_2}</p>}
                {text.weekly_plan.day_3 && <p className="text-zinc-300"><span className="text-zinc-500">Day 3:</span> {text.weekly_plan.day_3}</p>}
              </div>
            </div>
          )}
          
          {text.exercises?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold uppercase tracking-widest text-[#02b5ff] mb-3">Recommended Exercises</h4>
              <div className="grid gap-3">
                {text.exercises.slice(0, 6).map((ex, idx) => (
                  <div key={idx} className="bg-zinc-900/50 border border-zinc-800 p-4 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-white font-bold">{ex.name}</h5>
                      <span className={`text-xs px-2 py-1 rounded ${
                        ex.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                        ex.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {ex.difficulty}
                      </span>
                    </div>
                    <p className="text-zinc-400 text-sm mb-2">{ex.description}</p>
                    <p className="text-[#02b5ff] text-sm font-mono">{ex.sets_reps}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {text.progression_notes?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold uppercase tracking-widest text-[#02b5ff] mb-3">Progression Notes</h4>
              <ul className="space-y-2">
                {text.progression_notes.map((note, idx) => (
                  <li key={idx} className="text-zinc-300 text-sm flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#02b5ff] mt-0.5 flex-shrink-0" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    
    // Parse markdown text into clean formatted components
    const parseMarkdownLine = (line) => {
      // Remove leading/trailing whitespace
      line = line.trim();
      
      // Skip empty lines
      if (!line) return null;
      
      // Convert **bold** to styled spans
      line = line.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
      
      // Convert *italic* to styled spans
      line = line.replace(/\*([^*]+)\*/g, '<em class="text-zinc-300 italic">$1</em>');
      
      return line;
    };

    const renderParsedContent = (content) => {
      // Split into lines and process
      const lines = content.split('\n');
      const elements = [];
      let currentList = [];
      let inList = false;

      lines.forEach((line, idx) => {
        const trimmedLine = line.trim();
        
        // Skip empty lines but close any open list
        if (!trimmedLine) {
          if (inList && currentList.length > 0) {
            elements.push(
              <ul key={`list-${idx}`} className="space-y-2 my-3">
                {currentList.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                    <span className="text-[#02b5ff] mt-1">•</span>
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            );
            currentList = [];
            inList = false;
          }
          return;
        }

        // Handle bullet points (- or *)
        if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
          inList = true;
          const content = parseMarkdownLine(trimmedLine.slice(2));
          if (content) currentList.push(content);
          return;
        }

        // Close any open list before other content
        if (inList && currentList.length > 0) {
          elements.push(
            <ul key={`list-${idx}`} className="space-y-2 my-3">
              {currentList.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                  <span className="text-[#02b5ff] mt-1">•</span>
                  <span dangerouslySetInnerHTML={{ __html: item }} />
                </li>
              ))}
            </ul>
          );
          currentList = [];
          inList = false;
        }

        // Handle headings
        if (trimmedLine.startsWith('### ')) {
          elements.push(
            <h4 key={idx} className="text-sm font-bold uppercase tracking-widest text-[#02b5ff] mt-6 mb-3 flex items-center gap-2">
              <Target className="h-4 w-4" />
              {trimmedLine.slice(4)}
            </h4>
          );
          return;
        }
        if (trimmedLine.startsWith('## ')) {
          elements.push(
            <h3 key={idx} className="text-base font-bold text-white mt-6 mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#02b5ff]" />
              {trimmedLine.slice(3)}
            </h3>
          );
          return;
        }
        if (trimmedLine.startsWith('# ')) {
          elements.push(
            <h2 key={idx} className="text-lg font-bold text-white mt-6 mb-4 pb-2 border-b border-zinc-800">
              {trimmedLine.slice(2)}
            </h2>
          );
          return;
        }

        // Handle numbered lists (1. 2. etc)
        if (/^\d+\.\s/.test(trimmedLine)) {
          const content = parseMarkdownLine(trimmedLine.replace(/^\d+\.\s/, ''));
          const number = trimmedLine.match(/^(\d+)\./)?.[1] || '•';
          elements.push(
            <div key={idx} className="flex items-start gap-3 my-2">
              <span className="flex-shrink-0 w-6 h-6 bg-[#02b5ff]/20 text-[#02b5ff] rounded-full flex items-center justify-center text-xs font-bold">
                {number}
              </span>
              <p className="text-zinc-300 text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: content }} />
            </div>
          );
          return;
        }

        // Regular paragraph
        const content = parseMarkdownLine(trimmedLine);
        if (content) {
          elements.push(
            <p key={idx} className="text-zinc-300 text-sm leading-relaxed my-2" dangerouslySetInnerHTML={{ __html: content }} />
          );
        }
      });

      // Close any remaining open list
      if (currentList.length > 0) {
        elements.push(
          <ul key="final-list" className="space-y-2 my-3">
            {currentList.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                <span className="text-[#02b5ff] mt-1">•</span>
                <span dangerouslySetInnerHTML={{ __html: item }} />
              </li>
            ))}
          </ul>
        );
      }

      return elements;
    };

    return (
      <div className="recommendation-formatted space-y-1">
        {renderParsedContent(text)}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="ai-coach-section">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-[#02b5ff] to-[#ff025f] rounded-lg">
            <Brain className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white uppercase tracking-tight">AI Coach</h2>
            <p className="text-xs text-zinc-500">Evidence-based recommendations for {childName}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowChat(!showChat)}
            className={`border-zinc-700 ${showChat ? 'bg-[#02b5ff]/20 text-[#02b5ff] border-[#02b5ff]' : 'text-zinc-400 hover:text-white'}`}
            data-testid="toggle-chat-btn"
          >
            <MessageCircle className="h-4 w-4 mr-2" />
            {showChat ? 'Hide Chat' : 'Ask Question'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadTrainingPlan}
            disabled={downloadingPdf}
            className="border-zinc-700 text-zinc-400 hover:text-white hover:border-[#ff025f]"
            data-testid="download-pdf-btn"
          >
            {downloadingPdf ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download PDF
          </Button>
          {history.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowHistory(!showHistory)}
              className="border-zinc-700 text-zinc-400 hover:text-white"
              data-testid="toggle-history-btn"
            >
              {showHistory ? 'Hide History' : `History (${history.length})`}
            </Button>
          )}
        </div>
      </div>

      {/* Chat Section */}
      {showChat && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden" data-testid="chat-section">
          <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4 text-[#02b5ff]" />
              <span className="text-sm font-medium text-white">Chat with AI Coach</span>
            </div>
            <span className="text-xs text-zinc-500">Questions forwarded if AI can't help</span>
          </div>
          
          {/* Chat Messages */}
          <div className="h-64 overflow-y-auto p-4 space-y-4">
            {chatHistory.length === 0 ? (
              <div className="text-center text-zinc-500 text-sm py-8">
                <MessageCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Start a conversation with the AI Coach</p>
                <p className="text-xs mt-1">Ask about training, exercises, or athlete development</p>
              </div>
            ) : (
              chatHistory.map((msg, idx) => (
                <div key={msg.id || idx} className="space-y-2">
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="bg-[#02b5ff]/20 border border-[#02b5ff]/30 rounded-lg p-3 max-w-[80%]">
                      <p className="text-sm text-white">{msg.message}</p>
                      <p className="text-xs text-zinc-500 mt-1">
                        {msg.created_at ? new Date(msg.created_at).toLocaleTimeString() : ''}
                      </p>
                    </div>
                  </div>
                  {/* AI response */}
                  {msg.response && (
                    <div className="flex justify-start">
                      <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 max-w-[80%]">
                        <p className="text-sm text-zinc-300">{msg.response}</p>
                        {msg.forwarded && (
                          <div className="flex items-center gap-1 mt-2 text-xs text-green-400">
                            <Mail className="h-3 w-3" />
                            Forwarded to coaching team
                          </div>
                        )}
                        {msg.needs_human_followup && !msg.forwarded && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => forwardToHuman(msg.id)}
                            className="mt-2 text-xs text-[#ff025f] hover:text-white"
                          >
                            <Mail className="h-3 w-3 mr-1" />
                            Forward to Human
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>
          
          {/* Chat Input */}
          <div className="p-3 border-t border-zinc-800">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Ask the AI Coach..."
                className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#02b5ff]"
                data-testid="chat-input"
              />
              <Button
                onClick={() => sendChatMessage()}
                disabled={sendingMessage || !chatMessage.trim()}
                className="bg-[#02b5ff] hover:bg-[#0099dd] text-white"
                data-testid="send-message-btn"
              >
                {sendingMessage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </Button>
              <Button
                onClick={() => sendChatMessage(true)}
                disabled={sendingMessage}
                variant="outline"
                className="border-[#ff025f] text-[#ff025f] hover:bg-[#ff025f] hover:text-white"
                title="Request human help"
                data-testid="request-human-btn"
              >
                <User className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-xs text-zinc-600 mt-2">
              Click <User className="h-3 w-3 inline" /> to forward directly to bookings@eliteplayerperformance.co.uk
            </p>
          </div>
        </div>
      )}

      {/* Focus Area Selection */}
      <div className="bg-zinc-900/50 border border-zinc-800 p-4 rounded-lg">
        <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-3">
          Select Focus Areas (Optional)
        </h3>
        <div className="flex flex-wrap gap-2">
          {FOCUS_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => toggleFocus(option.id)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedFocus.includes(option.id)
                  ? 'bg-[#02b5ff] text-white'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700 hover:text-white'
              }`}
              data-testid={`focus-${option.id}`}
            >
              <span className="mr-2">{option.icon}</span>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <Button
        onClick={generateRecommendations}
        disabled={loading}
        className="w-full h-12 bg-gradient-to-r from-[#02b5ff] to-[#0099dd] hover:from-[#0099dd] hover:to-[#0088cc] text-white font-bold uppercase tracking-wider"
        data-testid="generate-recommendations-btn"
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Analyzing Performance Data...
          </>
        ) : (
          <>
            <Sparkles className="mr-2 h-5 w-5" />
            Generate AI Recommendations
          </>
        )}
      </Button>

      {/* History Section */}
      {showHistory && history.length > 0 && (
        <div className="bg-zinc-900/30 border border-zinc-800 p-4 rounded-lg">
          <h3 className="text-sm font-bold uppercase tracking-widest text-zinc-400 mb-4">
            Previous Recommendations
          </h3>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {history.map((rec, idx) => (
              <button
                key={rec.id || idx}
                onClick={() => {
                  setRecommendation(rec);
                  setShowHistory(false);
                }}
                className="w-full text-left bg-zinc-900/50 border border-zinc-800 p-3 rounded hover:border-[#02b5ff]/50 transition-colors"
                data-testid={`history-item-${idx}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium">
                    {rec.created_at 
                      ? new Date(rec.created_at).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'Recent'}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    rec.source === 'llm' || rec.source === 'AI (GPT-4o)'
                      ? 'bg-[#02b5ff]/20 text-[#02b5ff]'
                      : 'bg-zinc-700 text-zinc-400'
                  }`}>
                    {rec.source === 'llm' ? 'AI' : rec.source}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Results Section */}
      {loadingHistory ? (
        <div className="bg-zinc-900/30 border border-zinc-800 p-8 rounded-lg text-center">
          <Loader2 className="h-8 w-8 text-[#02b5ff] animate-spin mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">Loading recommendations...</p>
        </div>
      ) : recommendation ? (
        <div className="bg-zinc-900/30 border border-zinc-800 p-6 rounded-lg" data-testid="recommendation-result">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-800">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="h-4 w-4 text-[#02b5ff]" />
                <span className="text-white font-bold">AI Recommendations</span>
              </div>
              <p className="text-xs text-zinc-500">
                {recommendation.source === 'llm' || recommendation.source === 'AI (GPT-4o)'
                  ? 'Generated by GPT-4o using all historical data'
                  : 'Generated using rule-based analysis'}
              </p>
            </div>
            <Button
              onClick={downloadTrainingPlan}
              disabled={downloadingPdf}
              className="bg-[#ff025f] hover:bg-[#dd0050] text-white"
              data-testid="download-pdf-inline-btn"
            >
              {downloadingPdf ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download PDF Plan
            </Button>
          </div>

          <div className="recommendation-content">
            {formatRecommendation(recommendation.recommendation)}
          </div>

          {/* Auto-Generated Workout Plan */}
          {recommendation.workout_plan && (
            <div className="mt-6 pt-4 border-t border-zinc-800">
              <div className="flex items-center gap-2 mb-4">
                <Dumbbell className="h-5 w-5 text-[#ff025f]" />
                <h4 className="text-white font-bold">Auto-Generated Workout Plan</h4>
                <span className="text-xs bg-[#ff025f]/20 text-[#ff025f] px-2 py-1 rounded">NEW</span>
              </div>
              <p className="text-zinc-400 text-sm mb-4">{recommendation.workout_plan.description}</p>
              
              <div className="space-y-2">
                {recommendation.workout_plan.exercises?.map((ex, idx) => (
                  <div key={idx} className="bg-zinc-900/50 border border-zinc-700 p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[#02b5ff] font-bold">{idx + 1}</span>
                        <span className="text-white font-medium capitalize">{ex.name}</span>
                      </div>
                      <span className="text-xs text-zinc-500">{ex.sets} × {ex.reps}</span>
                    </div>
                    <div className="flex gap-2 text-xs">
                      <span className="bg-[#02b5ff]/20 text-[#02b5ff] px-2 py-0.5 rounded capitalize">{ex.body_part}</span>
                      {ex.target && <span className="bg-zinc-700 text-zinc-300 px-2 py-0.5 rounded capitalize">{ex.target}</span>}
                    </div>
                    {ex.instructions && ex.instructions.length > 0 && (
                      <div className="mt-2 text-xs text-zinc-400">
                        <span className="text-zinc-500">Instructions: </span>
                        {ex.instructions[0]}
                        {ex.instructions.length > 1 && <span className="text-[#02b5ff]"> +{ex.instructions.length - 1} more steps</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-zinc-500 mt-3 flex items-center gap-1">
                <CheckCircle className="h-3 w-3 text-green-500" />
                Workout plan saved to Exercise Library - view in Plans tab
              </p>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-zinc-800">
            <Button
              onClick={generateRecommendations}
              disabled={loading}
              variant="outline"
              className="border-zinc-700 text-zinc-400 hover:text-white hover:border-[#02b5ff]"
              data-testid="regenerate-btn"
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Generate New Recommendations
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-zinc-900/30 border border-zinc-800 border-dashed p-8 rounded-lg text-center" data-testid="no-recommendations">
          <Brain className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
          <h3 className="text-white font-bold mb-2">No Recommendations Yet</h3>
          <p className="text-zinc-500 text-sm mb-4">
            Click the button above to generate personalized training recommendations.
          </p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-[#02b5ff]/10 border border-[#02b5ff]/30 p-4 rounded-lg">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-[#02b5ff] mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-[#02b5ff] font-bold text-sm mb-1">How AI Coach Works</h4>
            <p className="text-[#02b5ff]/80 text-xs leading-relaxed">
              AI Coach analyzes all historical data to provide evidence-based recommendations.
              Use the chat to ask questions - if AI can't help, your question is automatically
              forwarded to bookings@eliteplayerperformance.co.uk
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
